import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import type { Result } from '@zxing/library';

/** Detect mobile so we can force back camera and hide camera picker. */
function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

interface QRScannerProps {
  isScanning: boolean;
  selectedCameraId: string;
  onQRCodeDetected: (qrData: string) => void;
  onError?: (error: string) => void;
  onCameraReady?: () => void;
}

/**
 * Stops every track on a stream. Use getTracks() so audio and video are both released.
 */
function stopMediaStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    track.stop();
    track.enabled = false;
  });
}

export default function QRScanner({
  isScanning,
  selectedCameraId,
  onQRCodeDetected,
  onError,
  onCameraReady
}: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const activeStreamRef = useRef<MediaStream | null>(null);
  const scannerControlsRef = useRef<IScannerControls | null>(null);
  const scannerControlsPromiseRef = useRef<Promise<IScannerControls> | null>(null);
  const pendingStopRef = useRef(false);
  const isProcessingRef = useRef(false);
  const scanSessionStartTimeRef = useRef<number>(Date.now());
  const onCameraReadyRef = useRef(onCameraReady);
  const onQRCodeDetectedRef = useRef(onQRCodeDetected);
  const onErrorRef = useRef(onError);
  const cameraReadyNotifiedRef = useRef(false);
  /** Incremented when tab becomes visible again so effect re-inits camera after visibility stop. */
  const [visibilityRestore, setVisibilityRestore] = useState(0);
  /** Tracks if we were hidden so we only restore when transitioning hidden -> visible. */
  const wasHiddenRef = useRef(false);

  const stopCamera = useRef(() => {
    pendingStopRef.current = true;

    const controls = scannerControlsRef.current;
    if (controls) {
      try {
        controls.stop();
      } catch {
        // ignore
      }
      scannerControlsRef.current = null;
    }

    const promise = scannerControlsPromiseRef.current;
    if (promise) {
      promise
        .then((c) => {
          try {
            c.stop();
          } catch {
            // ignore
          }
        })
        .catch(() => { });
      scannerControlsPromiseRef.current = null;
    }

    const reader = readerRef.current;
    if (reader) {
      try {
        const r = reader as unknown as { stopContinuousDecode?: () => void; reset?: () => void; stopAsyncDecode?: () => Promise<void> };
        if (r.stopContinuousDecode) r.stopContinuousDecode();
        if (r.reset) r.reset();
        if (r.stopAsyncDecode) r.stopAsyncDecode().catch(() => { });
      } catch {
        // ignore
      }
      readerRef.current = null;
    }

    stopMediaStream(activeStreamRef.current);
    activeStreamRef.current = null;

    const video = videoRef.current;
    if (video) {
      const srcStream = video.srcObject as MediaStream | null;
      stopMediaStream(srcStream);
      video.srcObject = null;
      video.pause();
      video.load();
    }
  });

  useEffect(() => {
    onCameraReadyRef.current = onCameraReady;
    onQRCodeDetectedRef.current = onQRCodeDetected;
    onErrorRef.current = onError;
  }, [onCameraReady, onQRCodeDetected, onError]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        wasHiddenRef.current = true;
        stopCamera.current();
      } else if (wasHiddenRef.current && isScanning) {
        wasHiddenRef.current = false;
        setVisibilityRestore((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isScanning]);

  useEffect(() => {
    if (!isScanning) {
      stopCamera.current();
      return;
    }

    let isMounted = true;
    let reader: BrowserMultiFormatReader | null = null;

    const initCamera = async () => {
      const video = videoRef.current;
      if (!video) return;

      try {
        isProcessingRef.current = false;
        cameraReadyNotifiedRef.current = false;
        scanSessionStartTimeRef.current = Date.now();
        pendingStopRef.current = false;

        const isMobile = isMobileDevice();
        const constraints: MediaStreamConstraints = {
          video: isMobile
            ? { facingMode: 'environment' }
            : selectedCameraId
              ? { deviceId: { exact: selectedCameraId } }
              : { facingMode: 'user' }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!isMounted) {
          stopMediaStream(stream);
          return;
        }

        activeStreamRef.current = stream;
        video.srcObject = stream;
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('muted', 'true');
        video.setAttribute('autoplay', 'true');

        await video.play();
        if (!isMounted) return;

        if (onCameraReadyRef.current) {
          cameraReadyNotifiedRef.current = true;
          onCameraReadyRef.current();
        }

        reader = new BrowserMultiFormatReader();
        readerRef.current = reader;

        const controlsPromise = reader.decodeFromStream(
          stream,
          video,
          (result: Result | undefined) => {
            if (!result || !isMounted || isProcessingRef.current) return;
            const text = result.getText();
            const now = Date.now();
            if (now - scanSessionStartTimeRef.current < 2000) return;

            isProcessingRef.current = true;
            stopCamera.current();
            onQRCodeDetectedRef.current(text);
          }
        );

        scannerControlsPromiseRef.current = controlsPromise;
        const controls = await controlsPromise;
        scannerControlsRef.current = controls;
        scannerControlsPromiseRef.current = null;

        if (pendingStopRef.current) {
          try {
            controls.stop();
          } catch {
            // ignore
          }
          scannerControlsRef.current = null;
        }
      } catch (err) {
        if (isMounted && onErrorRef.current) {
          onErrorRef.current('Failed to initialize camera');
        }
      }
    };

    initCamera();

    return () => {
      isMounted = false;
      cameraReadyNotifiedRef.current = false;
      stopCamera.current();
    };
  }, [isScanning, selectedCameraId, visibilityRestore]);

  useEffect(() => {
    if (!isScanning) stopCamera.current();
  }, [isScanning]);

  if (!isScanning) {
    return null;
  }

  return (
    <div className="relative w-full h-full overflow-hidden bg-black">
      <video
        ref={videoRef}
        className="w-full h-full min-h-0 object-cover relative z-0"
        autoPlay
        playsInline
        muted
        style={{ objectFit: 'cover' }}
      />

      <div className="absolute inset-0 flex items-start justify-center pointer-events-none pt-20 sm:pt-28">
        <div className="w-64 h-64 border-2 border-white/50 rounded-3xl relative">
          <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-xl" />
          <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-xl" />
          <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-xl" />
          <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-xl" />
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent shadow-[0_0_15px_rgba(59,130,246,0.8)] animate-scan" />
        </div>
      </div>

      <div className="absolute inset-0 flex items-start justify-center pointer-events-none pt-20 sm:pt-28">
        <div className="w-64 h-64 rounded-3xl shadow-[0_0_0_4000px_rgba(0,0,0,0.5)]" />
      </div>
    </div>
  );
}
